import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { TaskReminder, TaskReminderKind, TaskReminderStatus } from '../types';
import {
  assertSelfTaskReminderRecipient,
  isAllowedRecurringIntervalMinutes,
  isTaskReminderInFuture,
} from './taskReminderValidation';
import { db } from './firebase';

export { assertSelfTaskReminderRecipient, isTaskReminderInFuture } from './taskReminderValidation';

const COLLECTION = 'taskReminders';

function mapReminderDoc(id: string, data: Record<string, unknown>): TaskReminder {
  const toDate = (v: unknown): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return v;
    if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
      return (v as { toDate: () => Date }).toDate();
    }
    return undefined;
  };

  const rawKind = data.kind;
  const kind: TaskReminderKind =
    rawKind === 'recurring' || rawKind === 'once' ? rawKind : 'once';

  return {
    id,
    taskId: String(data.taskId ?? ''),
    projectId: String(data.projectId ?? ''),
    recipientUserId: String(data.recipientUserId ?? ''),
    createdByUserId: String(data.createdByUserId ?? ''),
    remindAt: toDate(data.remindAt) ?? new Date(),
    message: typeof data.message === 'string' ? data.message : undefined,
    kind,
    intervalMinutes:
      typeof data.intervalMinutes === 'number' && Number.isFinite(data.intervalMinutes)
        ? data.intervalMinutes
        : undefined,
    status: (data.status as TaskReminderStatus) ?? 'pending',
    sentAt: toDate(data.sentAt),
    lastSentAt: toDate(data.lastSentAt),
    createdAt: toDate(data.createdAt) ?? new Date(),
    updatedAt: toDate(data.updatedAt) ?? new Date(),
  };
}

export interface CreateTaskReminderParams {
  taskId: string;
  projectId: string;
  userId: string;
  remindAt: Date;
  message?: string;
  kind?: TaskReminderKind;
  intervalMinutes?: number;
}

export async function createTaskReminder(params: CreateTaskReminderParams): Promise<string> {
  const { taskId, projectId, userId, remindAt, message, kind = 'once', intervalMinutes } = params;
  assertSelfTaskReminderRecipient(userId, userId);
  if (!isTaskReminderInFuture(remindAt)) {
    throw new Error('Reminder time must be in the future.');
  }
  if (kind === 'recurring') {
    if (!intervalMinutes || !isAllowedRecurringIntervalMinutes(intervalMinutes)) {
      throw new Error('Choose a valid recurring interval.');
    }
  } else if (intervalMinutes != null) {
    throw new Error('Interval applies to recurring reminders only.');
  }

  const now = Timestamp.now();
  const docRef = await addDoc(collection(db, COLLECTION), {
    taskId,
    projectId,
    recipientUserId: userId,
    createdByUserId: userId,
    remindAt: Timestamp.fromDate(remindAt),
    message: message?.trim() || null,
    kind,
    intervalMinutes: kind === 'recurring' ? intervalMinutes : null,
    status: 'pending' as TaskReminderStatus,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

/** Ask the backend to deliver any due reminders for the signed-in user. */
export async function flushDueTaskReminders(): Promise<{ processed: number; delivered: number }> {
  const fn = httpsCallable<Record<string, never>, { processed: number; delivered: number }>(
    getFunctions(undefined, 'us-central1'),
    'flushDueTaskReminders',
  );
  const result = await fn({});
  return result.data;
}

/** When the tab stays open, flush shortly after the reminder is due. */
export function scheduleTaskReminderFlush(remindAt: Date): () => void {
  const delayMs = remindAt.getTime() - Date.now() + 2500;
  if (delayMs <= 0 || delayMs > 7 * 24 * 60 * 60 * 1000) {
    return () => {};
  }
  const timer = window.setTimeout(() => {
    void flushDueTaskReminders().catch((err) => {
      console.error('[scheduleTaskReminderFlush]', err);
    });
  }, delayMs);
  return () => window.clearTimeout(timer);
}

export async function cancelTaskReminder(reminderId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, reminderId), {
    status: 'cancelled',
    updatedAt: Timestamp.now(),
  });
}

export async function cancelPendingTaskRemindersForTask(taskId: string): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('taskId', '==', taskId),
      where('status', '==', 'pending'),
    ),
  );
  if (snap.empty) return 0;

  const batch = writeBatch(db);
  const now = Timestamp.now();
  snap.docs.forEach((d) => {
    batch.update(d.ref, { status: 'cancelled', updatedAt: now });
  });
  await batch.commit();
  return snap.size;
}

export async function cancelPendingTaskRemindersForTaskAndUser(
  taskId: string,
  userId: string,
): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('taskId', '==', taskId),
      where('recipientUserId', '==', userId),
      where('status', '==', 'pending'),
    ),
  );
  if (snap.empty) return 0;

  const batch = writeBatch(db);
  const now = Timestamp.now();
  snap.docs.forEach((d) => {
    batch.update(d.ref, { status: 'cancelled', updatedAt: now });
  });
  await batch.commit();
  return snap.size;
}

export function subscribeTaskReminders(
  taskId: string,
  userId: string,
  callback: (reminders: TaskReminder[]) => void,
): () => void {
  const q = query(
    collection(db, COLLECTION),
    where('taskId', '==', taskId),
    where('recipientUserId', '==', userId),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const reminders = snapshot.docs
        .map((d) => mapReminderDoc(d.id, d.data() as Record<string, unknown>))
        .sort((a, b) => a.remindAt.getTime() - b.remindAt.getTime());
      callback(reminders);
    },
    (err) => {
      console.error('[subscribeTaskReminders]', err);
      callback([]);
    },
  );
}
